import { Subscription } from 'rxjs';
import { Component, ElementRef, OnInit, OnDestroy } from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexGrid,
  ApexLegend,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
} from 'ng-apexcharts';
import { User } from 'src/app/models/User';
import { Control } from './../../models/Control';
import { UserCreditInfo } from './../../models/UserCreditInfo';
import { AuthService } from './../../shared/services/auth.service';
import { ChartData, DashboardService, RcsDailySeries } from './../../shared/services/dashboard.service';
import { ADMINISTRATOR } from 'src/app/shared/services/user.service';
import { plansCheckoutUrl } from 'src/data/plans';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import {
  findProductPrice,
  usageFromSummary,
  usageSummaryTotalMicros,
  walletPriceSourceLabel,
  walletProductUnitSuffix,
  WalletBalance,
  WalletError,
  WalletPrices,
  WalletProductPrice,
  WalletServiceUsage,
  WalletBilling,
  WalletUsageService,
  WalletUsageSummary,
} from 'src/app/models/Wallet';
import { classifyWalletError, WalletService } from 'src/app/shared/services/wallet.service';
import {
  MONEY_BALANCE_FRACTION_DIGITS,
  MONEY_DEFAULT_LOCALE,
  MoneyMicrosPipe,
} from 'src/app/shared/pipes/money-micros.pipe';
import { readChoicePreference, saveChoicePreference } from 'src/app/shared/utils/list-preferences';
import { AccountVersionService, INTERNATIONAL } from 'src/app/shared/services/account-version.service';
import { Subject } from 'rxjs';

/**
 * Fuso do contrato do resumo de consumo (17tm466t0ca): `from`/`to` e
 * `days[].date` são datas civis de Brasília, não do navegador.
 */
const SUMMARY_TIME_ZONE = 'America/Sao_Paulo';
const SUMMARY_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: SUMMARY_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export type ChartOptions = {
  colors: string[];
  series: ApexAxisChartSeries;
  chart: ApexChart;
  tooltip: ApexTooltip;
  xaxis: ApexXAxis;
  stroke: ApexStroke;
  dataLabels: ApexDataLabels;
  yaxis: ApexYAxis | ApexYAxis[];
  grid: ApexGrid;
  legend: ApexLegend;
  fill: ApexFill;
};

/**
 * Canal do cabeçalho (CU-86ak77za5): governa a tela inteira — tarifa, total
 * consumido e as séries do gráfico. `all` mostra tudo; um canal escolhido deixa
 * só a série dele.
 *
 * O filtro de dentro do gráfico (Tudo/Mensagens/Ligações) saiu: dois controles
 * de canal na mesma tela, cada um recortando um pedaço, era o que o Bruno pediu
 * para acabar. Agora que WhatsApp e IA têm série diária (o gasto em R$ vindo do
 * extrato da carteira), o controle de cima consegue governar o gráfico sem
 * deixá-lo vazio em canal nenhum.
 */
export type DashboardChannel = 'all' | 'sms' | 'whatsapp' | 'call' | 'ai' | 'rcs';

/**
 * Unidade da série. SMS e ligações são CONTAGEM, também para quem é cobrado pela
 * carteira (o gasto em R$ deles fica no card "Gasto por canal"); WhatsApp e IA são
 * DINHEIRO. As duas grandezas nunca dividem eixo: a de R$ vai para o eixo da direita.
 */
export type ChartSeriesUnit = 'count' | 'money';

interface ChartChannelSeries {
  id: DashboardChannel;
  name: string;
  unit: ChartSeriesUnit;
  /** `null`: a fonte da série falhou e ela não é desenhada. */
  data: number[] | null;
  color: string;
  /** Só nas séries em R$: o total em micros, sem passar por ponto flutuante. */
  totalMicros?: number;
  /** Série de detalhe: só aparece com o canal dela escolhido, nunca em "Tudo". */
  onlyWhenSelected?: boolean;
  /** `false` tira a série do total do período (lidos já estão contados nos enviados). */
  inTotal?: boolean;
}

/** Serviços da carteira que viram série em R$ no gráfico. */
const WALLET_CHART_SERVICES: ReadonlyArray<DashboardChannel> = ['whatsapp', 'ai'];

/** Janela do gráfico, em dias. O protótipo trava em 30. */
export type ChartPeriod = 7 | 15 | 30;

/**
 * Valores da paleta nacional, repetidos aqui só como último recurso do leitor de
 * token. Não são cor nova: são os mesmos literais de `_palette.scss`, e só
 * aparecem se `--sms-*` não estiver definido — o que na prática é bug de tema.
 */
const FALLBACK_PRIMARY = '#00be9a';
const FALLBACK_ACCENT_WARM = '#f28e2c';
const FALLBACK_ACCENT_COOL = '#5fabfb';
const FALLBACK_ACCENT_VIOLET = '#b18cff';
const FALLBACK_MUTED = 'rgba(255, 255, 255, 0.45)';
const FALLBACK_TEXT_SOFT = 'rgba(255, 255, 255, 0.8)';
const FALLBACK_BORDER_SOFT = 'rgba(255, 255, 255, 0.05)';
const FALLBACK_SURFACE_SOFT = 'rgba(255, 255, 255, 0.05)';

/**
 * Canais do cabeçalho, na ordem do protótipo. Fora da classe porque é tabela
 * fixa: nada aqui depende do estado do componente.
 */
const CHANNEL_OPTIONS: ReadonlyArray<{ id: DashboardChannel; label: string; icon: string }> = [
  { id: 'all', label: 'Tudo', icon: 'fa-th-large' },
  { id: 'sms', label: 'SMS', icon: 'fa-comment' },
  { id: 'whatsapp', label: 'WhatsApp', icon: 'fa-whatsapp' },
  { id: 'call', label: 'Ligações', icon: 'fa-phone' },
  { id: 'ai', label: 'IA', icon: 'fa-magic' },
  // Só para conta com RCS (ver `showsRcs`).
  { id: 'rcs', label: 'RCS', icon: 'fa-commenting' },
];

/** Chave do canal escolhido no localStorage (`shared/utils/list-preferences`). */
const CHANNEL_PREFERENCE = 'dashboard.channel';

const moneyPipe = new MoneyMicrosPipe();

@Component({
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  controls: Control[] = [];
  lastCalls: any[] = [];

  user: User;
  creditInfo: UserCreditInfo;

  // Uma flag por requisição (CU-86akkqppg): cada bloco revela o próprio
  // conteúdo quando A SUA chamada responde, em vez de a tela inteira esperar a
  // mais lenta das cinco.
  loadingCredits = true;
  loadingMessages = true;
  loadingCalls = true;
  loadingSents = true;
  loadingCallSents = true;

  /**
   * Falha das duas listas (CU-86akkvp5q). Sem elas, erro caía no estado vazio
   * e a tela afirmava "Nenhuma mensagem enviada!" sobre algo que não sabia.
   */
  messagesError = false;
  callsError = false;

  // Carteira (CU-86akm3435): duas requisições próprias, com o mesmo desenho de
  // esqueleto por bloco — o saldo aparece sem esperar a varredura do extrato,
  // que é a chamada lenta desta tela.
  loadingBalance = true;
  loadingUsage = true;

  balance: WalletBalance = null;
  balanceError: WalletError = null;
  usage: WalletServiceUsage[] = [];
  usageError: WalletError = null;

  /**
   * Resumo do consumo do período, somado no servidor sem paginação nem teto de
   * lançamentos (17tm466t0ca). `null` é resumo que não veio (carteira
   * desligada ou fora do ar) — e aí as séries em R$ (WhatsApp e IA) não são
   * desenhadas, em vez de aparecerem zeradas. A contagem de SMS e ligação do
   * gráfico NÃO depende dele: vem de `user_counters` (CU-17tm466qj1t).
   */
  private usageSummary: WalletUsageSummary | null = null;

  // --- Preço vigente (CU-86akn0zp6) ------------------------------------------
  prices: WalletPrices = null;
  pricesError: WalletError = null;
  loadingPrices = true;

  /**
   * Canal do cabeçalho: o último escolhido neste navegador (09/10/2026, M2 do
   * Misael — o seletor voltava para "Tudo" a cada refresh), pelo mesmo mecanismo
   * do "Reverso" do envio em massa. Sem escolha salva, ou com uma que não é mais
   * canal, abre em `all`.
   */
  channel: DashboardChannel = readChoicePreference(CHANNEL_PREFERENCE, CHANNEL_OPTIONS.map(option => option.id), 'all');

  /** Janela do gráfico. Sete dias é o que a tela já abria. */
  chartDays: ChartPeriod = 7;

  readonly chartPeriodOptions: ReadonlyArray<ChartPeriod> = [7, 15, 30];

  /**
   * Valores mascarados.
   *
   * Dinheiro na tela é o que mais incomoda quando alguém olha por cima do
   * ombro, e o painel é aberto em reunião. Fica na memória do componente de
   * propósito: gravar a escolha faria o saldo abrir escondido no dia seguinte,
   * quando o motivo de escondê-lo já passou.
   */
  valuesHidden = false;

  /** Placeholder do valor escondido. Mantém a forma de dinheiro sem o número. */
  readonly maskedValue = 'R$ •••••';

  /** Abertura do modal de recarga, empurrada para o `app-recharge-modal`. */
  showRechargeModal = new Subject<string>();

  /** `true` quando a conta está operando no internacional. */
  isInternational = false;

  /**
   * O card de saldo arredonda em 2 casas, como o modal do header e a tela da
   * carteira; o gasto por canal mantém o teto de 6, porque débito de IA da
   * ordem de R$ 0,0007 exibido como `R$ 0,00` some da tela. Ver o docblock do
   * `MoneyMicrosPipe`.
   */
  readonly balanceLocale = MONEY_DEFAULT_LOCALE;
  readonly balanceFractionDigits = MONEY_BALANCE_FRACTION_DIGITS;

  /**
   * O gráfico espera as duas séries de contagem (SMS e ligações): desenhá-lo
   * com metade delas mostraria um vale onde só falta dado. As séries em R$ NÃO
   * seguram o esqueleto — o resumo da carteira é a chamada mais lenta da tela,
   * e elas entram no gráfico quando chegam.
   */
  get loadingChart(): boolean {
    return this.loadingSents || this.loadingCallSents;
  }

  messagesChartOptions: Partial<ChartOptions>;
  subscriptions: Subscription[] = [];

  /**
   * Uma carga por bloco do painel (regra 7). Trocar o período ou o usuário refaz as cargas, e a
   * anterior do mesmo bloco é cancelada: sem isso a varredura de 30 dias, mais lenta, chegava
   * depois da de 7 e gravava consumo e extrato de outra janela ao lado do gráfico de 7 dias.
   */
  private loads = new Map<string, Subscription>();

  private track(block: string, subscription: Subscription): void {
    this.loads.get(block)?.unsubscribe();
    this.loads.set(block, subscription);
  }

  messagesChartDateField: boolean = false;
  startedDay: Date;
  endedDay: Date;

  now: Date = new Date(Date.now());
  minDate: Date;

  /** Somas do período já recortadas pelo canal — é o número grande do gráfico. */
  periodTotal = 0;
  periodDailyAverage = 0;

  /** Gasto em R$ (micros) das séries da carteira visíveis no gráfico. */
  periodSpentMicros = 0;

  /** O gráfico tem série de contagem / de R$ visível. Decide o que o topo mostra. */
  chartHasCountSeries = false;
  chartHasMoneySeries = false;

  /** Algum canal de contagem escolhido ficou sem fonte (`daily-sents`/`daily-call-sents` falhou). */
  chartCountUnavailable = false;

  private messagesChartData: ChartData;
  private callsChartData: ChartData;

  /**
   * `daily-sents` / `daily-call-sents` falharam no período. Série de contagem sem fonte não é
   * desenhada: zerada ela diria "0 envios" sobre o que a tela não conseguiu ler — o mesmo zero
   * falso da CU-17tm466qj1t, só que por erro de rede.
   */
  private sentsFailed = false;
  private callSentsFailed = false;

  /**
   * Série RCS do período (17tm466frtx), o bloco `rcs` do `GET /daily-sents`. `null` é conta
   * sem RCS: o backend só manda o bloco com o RCS liberado (flag da conta e canal global).
   */
  rcsSeries: RcsDailySeries = null;

  /**
   * O RCS aparece no dashboard (canal no cabeçalho, série no gráfico e o bloco de números):
   * flag da conta (`requiresRcs` do menu) E o bloco na resposta — a flag sozinha não sabe
   * do kill-switch global, e o bloco sozinho não deveria aparecer para conta sem a flag.
   */
  get showsRcs(): boolean {
    return !!this.user?.rcs_enabled && !!this.rcsSeries;
  }

  /** Parte dos enviados por RCS que chegou (`—` sem envio no período). */
  /** Há entregue/lido do RCS? Sem o retorno do broker, o bloco mostra só enviados e cliques. */
  get rcsTracked(): boolean {
    return this.rcsSeries?.provider_status !== false;
  }

  get rcsDeliveredShare(): string {
    return share(this.rcsSeries?.totals.delivered, this.rcsSeries?.totals.sent);
  }

  /** Parte dos entregues que foi lida — a mesma base do funil do relatório do envio. */
  get rcsReadShare(): string {
    return share(this.rcsSeries?.totals.read, this.rcsSeries?.totals.delivered);
  }

  /** Parte das mensagens do período (RCS + SMS) que saiu como SMS de fallback. */
  get rcsFallbackShare(): string {
    const totals = this.rcsSeries?.totals;

    return share(totals?.sms_fallback, totals ? totals.sent + totals.sms_fallback : 0);
  }

  get isAdministrator(): boolean {
    return this.user.profile_id === ADMINISTRATOR;
  }

  /**
   * Coorte da carteira. O recorte sai da flag da conta, NUNCA de "tem saldo na
   * carteira": quem migrou com resto no balde tem saldo e continua sendo cobrado
   * por unidade — mostrar-lhe a tela em R$ seria trocar a cobrança dele de lugar.
   */
  get isWalletCohort(): boolean {
    return !!(this.user && this.user.wallet_billing_enabled);
  }

  /**
   * De onde sai cada canal (`GET /wallet/billing`). Todo cliente pergunta desde
   * 29/09/2026: é a resposta que decide se o card da carteira aparece fora da
   * coorte (WhatsApp e IA saem da carteira para todo mundo). Fora da coorte SMS e
   * ligação continuam pacote por definição — o backend responde `credits`.
   */
  billing: WalletBilling | null = null;

  /**
   * A home fala de carteira (sem o banner de planos por pacote, sem os tokens de IA por
   * pacote, com o gasto por canal): a coorte da sessão OU um produto de pacote (SMS/ligação)
   * já cobrado pela carteira no billing. Sem o billing, quem migrou o SMS depois do login
   * (sessão com `wallet_billing_enabled` antigo) via a oferta de pacote de SMS e não via o
   * gasto em R$ do card "Gasto por canal".
   */
  get showsWalletPanels(): boolean {
    return this.isWalletCohort || this.billing?.sms === 'wallet' || this.billing?.call === 'wallet';
  }

  /**
   * Card da carteira em R$. A coorte vê sempre (como antes); fora dela, quando
   * algum canal é cobrado pela carteira — decisão de 29/09/2026: o cliente que
   * paga WhatsApp em R$ precisa ver o saldo que está pagando.
   */
  get showWalletCard(): boolean {
    if (this.isWalletCohort) { return true; }

    const billing = this.billing;

    return !!billing && (billing.whatsapp === 'wallet' || billing.ai === 'wallet'
      || billing.sms === 'wallet' || billing.call === 'wallet');
  }

  /**
   * Saldo de pacote na home. Decisão de 28/09/2026: SMS e ligação seguem por
   * pacote e quantidade até a flag do canal ligar — inclusive para a coorte, que
   * antes perdia esses cartões e ficava sem ver o número que decide se o envio
   * sai. Na coorte o cartão só aparece com a resposta na mão: sem ela, mostrar
   * pacote de um canal que já saiu para a carteira seria o erro oposto.
   *
   * Desde 01/10/2026 quem decide é o produto migrado (`wallet_sms_billed` /
   * `wallet_call_billed`), não a coorte: com a resposta do billing na mão ela manda,
   * dentro ou fora da coorte. A coorte só decide enquanto a resposta não chega.
   */
  get showSmsCredits(): boolean {
    return this.billing ? this.billing.sms === 'credits' : !this.isWalletCohort;
  }

  get showCallCredits(): boolean {
    return this.billing ? this.billing.call === 'credits' : !this.isWalletCohort;
  }

  /**
   * O que o saldo em R$ paga. "Todos os produtos" só é verdade quando nenhum canal
   * ficou no pacote; com SMS e ligação no pacote (28/09/2026) a frase mandaria o
   * cliente recarregar R$ para mandar SMS.
   */
  get walletScopeHint(): string {
    const walletChannels = [
      this.billing?.sms === 'wallet' ? 'SMS' : null,
      this.billing?.call === 'wallet' ? 'ligações' : null,
    ].filter(Boolean);

    if (this.billing && walletChannels.length === 0) {
      return 'Saldo em R$ para WhatsApp e IA';
    }

    if (this.billing && walletChannels.length < 2) {
      return `Saldo em R$ para WhatsApp, IA e ${walletChannels[0]}`;
    }

    return 'Saldo único para todos os produtos';
  }

  /** Moeda declarada pelo saldo. Sem ela o pipe formata o número sem símbolo. */
  get currency(): string {
    return (this.balance && this.balance.currency) || '';
  }

  /** 403 é conta sem carteira habilitada — estado previsto, não falha. */
  get isWalletDisabled(): boolean {
    return this.balanceError !== null && this.balanceError.kind === 'disabled';
  }

  get hasUsage(): boolean {
    return this.usage.length > 0;
  }

  // --- Cabeçalho de canal (CU-86akn0zp6) -------------------------------------

  /**
   * Canais oferecidos no cabeçalho.
   *
   * Ligações somem no internacional pela mesma razão que já somem do menu: a
   * operação internacional não disca. O filtro é aqui e não no template para o
   * canal escolhido poder voltar para `all` ("Tudo") quando o realm troca com
   * `call` selecionado — caso contrário a tarifa ficaria parada num produto que a
   * tela não oferece mais. A escolha salva no navegador não é apagada nesse caso.
   */
  get channelOptions(): ReadonlyArray<{ id: DashboardChannel; label: string; icon: string }> {
    return CHANNEL_OPTIONS.filter(option => !(this.isInternational && option.id === 'call')
      && (option.id !== 'rcs' || this.showsRcs));
  }

  /** Preço do canal escolhido. `null` é carteira que não devolveu o produto. */
  get selectedPrice(): WalletProductPrice {
    return findProductPrice(this.prices, this.channel);
  }

  /** `true` quando o produto não tem preço resolvido — o envio não sai. */
  get isSelectedPriceBlocked(): boolean {
    const price = this.selectedPrice;

    return !price || price.blocked || price.unit_price_micros === null;
  }

  get selectedPriceSourceLabel(): string {
    const price = this.selectedPrice;

    return price ? walletPriceSourceLabel(price.source) : '';
  }

  get selectedPriceSuffix(): string {
    return walletProductUnitSuffix(this.channel);
  }

  /**
   * `Tudo` e IA não têm uma tarifa só: `Tudo` mistura produtos, e a IA é
   * cobrada por token, fora de `/wallet/prices`. Nos dois a tela diz isso em vez
   * de afirmar "sem preço definido", que leria como envio bloqueado.
   */
  get selectedRateNote(): string {
    if (this.channel === 'all') { return 'varia por canal'; }
    if (this.channel === 'ai') { return 'cobrada por tokens'; }
    // RCS tem dois preços (Basic e Single), escolhidos mensagem a mensagem.
    if (this.channel === 'rcs') { return 'varia por formato (Basic ou Single)'; }
    // SMS e ligação por pacote (28/09/2026): o preço de lista da carteira não é o
    // que este cliente paga por esse canal.
    if ((this.channel === 'sms' || this.channel === 'call') && this.billing?.[this.channel] === 'credits') {
      return 'por pacote (quantidade)';
    }
    return '';
  }

  /** Gasto do canal escolhido no período. `null` é canal sem consumo. */
  get selectedUsage(): WalletServiceUsage {
    return this.usage.find(entry => entry.service === this.channel) || null;
  }

  /**
   * Em `Tudo`, o total consumido é o `total_spent_micros` do resumo: a soma de
   * TODOS os serviços que a carteira cobrou, inclusive os que este painel ainda
   * não conhece pelo nome (ex. RCS).
   */
  get selectedUsageMicros(): number {
    if (this.channel === 'all') {
      return usageSummaryTotalMicros(this.usageSummary);
    }

    // RCS sai da carteira como dois serviços (`rcs_basic` e `rcs_single`).
    if (this.channel === 'rcs') {
      return this.usage
        .filter(entry => entry.service === 'rcs_basic' || entry.service === 'rcs_single')
        .reduce((total, entry) => total + entry.spentMicros, 0);
    }

    return this.selectedUsage ? this.selectedUsage.spentMicros : 0;
  }

  /**
   * O resumo não veio (carteira fora do ar) e o canal escolhido tem série em
   * R$. Carteira desligada (403) não entra: não é falha, é conta sem gasto em
   * R$ para mostrar.
   */
  get chartSpendUnavailable(): boolean {
    if (this.loadingUsage) { return false; }

    const failed = !!this.usageError && this.usageError.kind !== 'disabled';
    const wantsMoney = this.channel === 'all' || WALLET_CHART_SERVICES.includes(this.channel);

    return failed && wantsMoney;
  }

  /** Checkout externo da assinatura: não há tela de planos no painel. */
  get plansCheckoutUrl(): string {
    return plansCheckoutUrl(this.user ? this.user.id : null);
  }

  /**
   * Banner de planos: para quem a home fala de carteira (`showsWalletPanels`: coorte OU
   * SMS/ligação já cobrado pela carteira) E não tem assinatura. É ele que entra no lugar do
   * `app-plan-message` de pacote, que some nesse mesmo caso — fora da coorte, quem migrou o SMS
   * ficava sem nenhum dos dois.
   *
   * Enquanto `/wallet/prices` não responde ele não aparece — piscar "assine" na
   * cara de quem já assinou é pior do que aparecer meio segundo depois.
   */
  get showPlansBanner(): boolean {
    return this.showsWalletPanels && !this.loadingPrices && !!this.prices && this.prices.subscription === null;
  }

  /**
   * Legenda do total do gráfico. O número somado é o das séries de contagem
   * visíveis, e chamar de "mensagens" o total que inclui ligações seria contar
   * errado em voz alta.
   */
  get periodTotalLabel(): string {
    const unit = this.channel === 'call'
      ? 'ligações'
      : this.channel === 'sms' || this.channel === 'rcs' ? 'mensagens' : 'envios';

    return `${unit} nos ${this.chartDays} dias`;
  }

  /** Canais em R$ que o gráfico desenhou (só WhatsApp e IA; SMS e ligação são contagem). */
  chartMoneyChannels: string[] = [];

  /** Legenda do gasto em R$ do gráfico: os canais que de fato estão nele. */
  get periodSpentLabel(): string {
    const names = this.chartMoneyChannels;
    const target = names.length > 1
      ? `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`
      : (names[0] || 'WhatsApp e IA');

    return `gastos com ${target} nos ${this.chartDays} dias`;
  }

  get hasAiTokens(): boolean {
    if (!this.creditInfo || !this.user) { return false; }
    return !!this.user.ai_enabled;
  }

  get availableCredits(): number {
    return this.creditInfo.credits - this.creditInfo.total_reserved;
  }

  get availableCallCredits(): number {
    return (this.creditInfo.call_credits || 0) - (this.creditInfo.call_total_reserved || 0);
  }

  constructor(
    private dashboardService: DashboardService,
    private authService: AuthService,
    private walletService: WalletService,
    private accountVersionService: AccountVersionService,
    private element: ElementRef<HTMLElement>
  ) {
    this.startedDay = new Date();
    this.startedDay.setDate(this.now.getDate() - 7); // 7 dias atrás
    this.endedDay = new Date(this.now); // Hoje

    // Set minimum date to 90 days ago
    this.minDate = new Date();
    this.minDate.setDate(this.now.getDate() - 90);
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.authService.userChanged.subscribe(() => {
        this.loadDashboard();
      })
    );

    // O realm decide quais canais o cabeçalho oferece. A primeira emissão do
    // BehaviorSubject é a carga inicial; a troca reposiciona o canal escolhido
    // antes que a tela peça a tarifa de um produto que sumiu.
    this.subscriptions.push(
      this.accountVersionService.getVersion().subscribe((version) => {
        this.isInternational = version === INTERNATIONAL;

        // Não grava: é o realm que tira Ligações da tela, não uma escolha do usuário.
        if (this.isInternational && this.channel === 'call') {
          this.channel = 'all';
          this.redrawChart();
        }
      })
    );

    // Antes das chamadas, não depois: o desenho vazio existe só para revelar o
    // seletor de data, e rodando por último ele apagaria a série de quem já
    // respondeu — hoje ninguém responde tão rápido, mas o número do cabeçalho
    // passaria a zero se alguém respondesse.
    this.showMessagesChart(null, null);

    this.loadDashboard();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.loads.forEach(sub => sub.unsubscribe());
    this.showRechargeModal.complete();
  }

  loadDashboard(): void {
    this.user = this.authService.getUser();

    if(this.startedDay > this.now){
      return;
    }

    // Depois da guarda: com data inválida nenhuma chamada sai, e ligar as flags
    // antes deixaria esqueleto brilhando à espera de requisição que não veio.
    this.loadingCredits = true;
    this.loadingMessages = true;
    this.loadingCalls = true;

    // A coorte vê a carteira em R$ e, dos cartões de unidade, só os canais que
    // ainda são pacote (SMS e ligação enquanto a flag do canal estiver desligada).
    if (this.isWalletCohort) {
      this.loadWallet();
      this.loadPackageCreditsForCohort();
    } else {
      // Fora da coorte SMS e ligação seguem em unidades, mas WhatsApp e IA já são
      // cobrados da carteira para todo mundo (flags globais). O resumo alimenta
      // as séries em R$ do gráfico e, com a resposta do billing, o card da
      // carteira (saldo e tarifa) aparece também aqui (29/09/2026).
      this.loadWalletUsage();
      this.loadBillingForLegacy();

      // Erro tem que baixar a flag junto com o sucesso. Sem isso o esqueleto
      // brilha para sempre — e é pior do que o overlay que saiu daqui, porque
      // promete conteúdo que não vem.
      this.track('credits',
        this.dashboardService.userCreditsInfo().subscribe({
          next: (creditInfo) => {
            this.creditInfo = creditInfo;
            this.loadingCredits = false;
          },
          error: () => (this.loadingCredits = false),
        })
      );
    }

    this.loadLastMessages();
    this.loadLastCalls();
    this.loadPeriodSeries();
  }

  /**
   * As séries do gráfico, que dependem do período. Trocar o período refaz só elas e o resumo
   * do período (`loadWalletUsage`): billing, créditos, saldo, tarifa e últimas mensagens não
   * dependem dele, e refazê-los punha os cartões em esqueleto a cada clique.
   */
  private loadPeriodSeries(): void {
    const startDate = this.formatDate(this.startedDay);

    // Toda recarga parte do zero, também a da troca de usuário (`userChanged`): a série da
    // carga anterior, com a fonte agora em erro, escondia a falha atrás do dado velho.
    this.messagesChartData = null;
    this.callsChartData = null;
    this.sentsFailed = false;
    this.callSentsFailed = false;

    this.loadingSents = true;
    this.loadingCallSents = true;

    this.track('dailySents',
      this.dashboardService.dailySents(startDate).subscribe({
        next: (data) => {
          this.messagesChartData = data;
          this.sentsFailed = false;
          this.applyRcsSeries(data.rcs || null);
          this.redrawChart();
          this.loadingSents = false;
        },
        error: () => {
          this.sentsFailed = true;
          this.applyRcsSeries(null);
          this.redrawChart();
          this.loadingSents = false;
        },
      }),
    );

    this.track('dailyCallSents',
      this.dashboardService.dailyCallSents(startDate).subscribe({
        next: (data) => {
          this.callsChartData = data;
          this.callSentsFailed = false;
          this.redrawChart();
          this.loadingCallSents = false;
        },
        error: () => {
          this.callSentsFailed = true;
          this.redrawChart();
          this.loadingCallSents = false;
        },
      }),
    );
  }

  /**
   * Guarda a série RCS que chegou com a de SMS. Sem ela (conta sem RCS, canal desligado ou
   * falha) o canal RCS sai do cabeçalho e, se era o escolhido, a tela volta para "Tudo".
   */
  private applyRcsSeries(series: RcsDailySeries | null): void {
    this.rcsSeries = series;

    if (this.channel === 'rcs' && !this.showsRcs) {
      this.channel = 'all';
    }
  }

  /** Últimas mensagens; também é o "Tentar novamente" do bloco. */
  loadLastMessages(): void {
    this.loadingMessages = true;
    this.messagesError = false;
    this.track('lastMessages',
      this.dashboardService.lastMessages().subscribe({
        next: (controls) => {
          this.controls = controls;
          this.loadingMessages = false;
        },
        error: () => {
          this.controls = [];
          this.messagesError = true;
          this.loadingMessages = false;
        },
      })
    );
  }

  /** Últimas ligações; também é o "Tentar novamente" do bloco. */
  loadLastCalls(): void {
    this.loadingCalls = true;
    this.callsError = false;
    this.track('lastCalls',
      this.dashboardService.lastCalls().subscribe({
        next: (calls) => {
          this.lastCalls = calls;
          this.loadingCalls = false;
        },
        error: () => {
          this.lastCalls = [];
          this.callsError = true;
          this.loadingCalls = false;
        },
      })
    );
  }

  /**
   * Coorte com canal ainda no pacote: pergunta de onde sai cada canal e, se
   * algum for pacote, busca os créditos para os cartões desse canal.
   */
  private loadPackageCreditsForCohort(): void {
    // Os créditos só são pedidos no `next` do billing novo; sem cancelar aqui, a carga anterior
    // seguia em voo quando o billing novo não pede créditos (ou falha) e gravava o `creditInfo`
    // de outro usuário ou janela depois.
    this.loads.get('credits')?.unsubscribe();
    this.loads.delete('credits');

    this.track('billing',
      this.walletService.getBilling().subscribe({
        next: (billing) => {
          this.billing = billing;
          this.redrawChart();

          if (!this.showSmsCredits && !this.showCallCredits) {
            this.loadingCredits = false;
            return;
          }

          this.track('credits',
            this.dashboardService.userCreditsInfo().subscribe({
              next: (creditInfo) => {
                this.creditInfo = creditInfo;
                this.loadingCredits = false;
              },
              error: () => (this.loadingCredits = false),
            })
          );
        },
        // Sem a resposta a coorte fica como era (só a carteira): melhor faltar
        // um cartão do que mostrar pacote de canal que talvez já seja R$.
        error: () => {
          this.billing = null;
          this.loadingCredits = false;
        },
      })
    );
  }

  /**
   * Fora da coorte: pergunta de onde sai cada canal e, se algum sair da carteira,
   * carrega saldo e tarifa para o card. O extrato já foi pedido pelo painel.
   * Falha aqui só esconde o card — os cartões de pacote não dependem dela.
   */
  private loadBillingForLegacy(): void {
    this.track('billing',
      this.walletService.getBilling().subscribe({
        next: (billing) => {
          this.billing = billing;
          this.redrawChart();

          if (this.showWalletCard) {
            this.loadWalletSummary();
          }
        },
        error: () => (this.billing = null),
      })
    );
  }

  loadWallet(): void {
    this.loadWalletSummary();
    this.loadWalletUsage();
  }

  /** Saldo e tarifa da carteira — sem o extrato, que tem varredura própria. */
  private loadWalletSummary(): void {
    this.loadingBalance = true;
    this.loadingPrices = true;
    this.balanceError = null;
    this.pricesError = null;

    // Terceira flag, e não a do saldo: o preço é outra rota e a tarifa não
    // pode segurar o número que o cliente veio ver.
    this.track('prices',
      this.walletService.getPrices().subscribe({
        next: (prices) => {
          this.prices = prices;
          this.loadingPrices = false;
        },
        error: (error) => {
          this.prices = null;
          this.pricesError = classifyWalletError(error);
          this.loadingPrices = false;
        },
      }),
    );

    this.track('balance',
      this.walletService.getBalance().subscribe({
        next: (balance) => {
          this.balance = balance;
          this.loadingBalance = false;
        },
        error: (error) => {
          // Zero NÃO entra aqui: a carteira indisponível não é carteira vazia, e
          // trocar uma pela outra faz o cliente achar que o dinheiro sumiu.
          this.balance = null;
          this.balanceError = classifyWalletError(error);
          this.loadingBalance = false;
        },
      }),
    );
  }

  /**
   * Resumo do consumo do período (17tm466t0ca). Uma só chamada alimenta o
   * gasto por canal da coorte, o total consumido e as séries em R$ (WhatsApp
   * e IA) do gráfico — pedir duas vezes o mesmo período
   * era dobrar a chamada mais lenta da tela.
   *
   * A soma é feita na carteira (`GET /wallet/usage-summary`, um `GROUP BY`),
   * não varrendo o extrato aqui: sem teto de lançamentos, por isso contas que
   * enviam muito (migradas para a carteira) deixaram de aparecer vazias.
   */
  loadWalletUsage(): void {
    this.loadingUsage = true;
    this.usageError = null;

    this.track('usage',
      this.walletService
        .getUsageSummary(this.summaryDate(this.startedDay), this.summaryDate(this.endedDay))
        .subscribe({
          next: (summary) => {
            this.usageSummary = summary;
            this.usage = usageFromSummary(summary);
            this.loadingUsage = false;
            this.redrawChart();
          },
          error: (error) => {
            this.usage = [];
            this.usageSummary = null;
            this.usageError = classifyWalletError(error);
            this.loadingUsage = false;
            this.redrawChart();
          },
        })
    );
  }

  /** Canal do cabeçalho: muda a tarifa, o total consumido E o gráfico. */
  selectChannel(channel: DashboardChannel): void {
    if (this.channel === channel) { return; }

    this.channel = channel;
    saveChoicePreference(CHANNEL_PREFERENCE, channel);
    this.redrawChart();
  }

  toggleValuesVisibility(): void {
    this.valuesHidden = !this.valuesHidden;
  }

  openRecharge(): void {
    this.showRechargeModal.next('');
  }

  /** Compra registrada: o saldo e o consumo na tela já são de antes dela. */
  onRechargeCompleted(): void {
    this.loadWallet();
  }

  /**
   * Janela do gráfico.
   *
   * Recalcula as duas pontas e refaz o que depende do período (`loadPeriod`) — inclusive o
   * resumo da carteira, que usa o MESMO período. Deixar o consumo no recorte antigo faria
   * o total consumido descrever uma janela e o gráfico outra, lado a lado.
   */
  selectChartPeriod(days: ChartPeriod): void {
    if (this.chartDays === days) { return; }

    this.chartDays = days;

    this.endedDay = new Date(this.now);
    this.startedDay = new Date(this.now);
    this.startedDay.setDate(this.now.getDate() - days);

    this.usageSummary = null;

    this.loadPeriod();
  }

  /** O que muda com o período: as séries do gráfico e o resumo da carteira (gasto por canal e séries em R$). */
  private loadPeriod(): void {
    if (this.startedDay > this.now) {
      return;
    }

    this.loadWalletUsage();
    this.loadPeriodSeries();
  }

  private redrawChart(): void {
    this.showMessagesChart(this.messagesChartData, this.callsChartData);
  }

  showMessagesChart(messagesData: ChartData, callsData: ChartData): void {
    const labels = (messagesData ? messagesData.labels : (callsData ? callsData.labels : [])) || [];
    const days = this.chartDayKeys(labels.length);

    const whatsappMicros = this.dailySpendMicros('whatsapp', days);
    const aiMicros = this.dailySpendMicros('ai', days);

    // SMS e ligação são sempre CONTAGEM (`daily-sents` / `daily-call-sents`), também
    // para quem é cobrado pela carteira. De 07/10 a 19/10/2026 a série desses canais
    // virava o gasto em R$ do extrato, porque o `user_counters` não andava no débito
    // da carteira; com isso o título "N envios" (que só soma contagens) mostrava 0 e,
    // sem extrato, a série de SMS sumia. O back passou a contar a carteira
    // (CU-17tm466qj1t) e a ligação já contava (`call4u_leads`). O gasto em R$ desses
    // canais fica no card "Gasto por canal"; aqui só WhatsApp e IA saem em R$.
    const channels: ChartChannelSeries[] = [
      {
        id: 'sms',
        name: 'SMS',
        unit: 'count',
        data: messagesData ? messagesData.data : (this.sentsFailed ? null : []),
        color: this.token('--sms-primary', FALLBACK_PRIMARY),
      },
      {
        id: 'call',
        name: 'Ligações',
        unit: 'count',
        data: callsData ? callsData.data : (this.callSentsFailed ? null : []),
        color: this.token('--sms-accent-warm', FALLBACK_ACCENT_WARM),
      },
      {
        id: 'whatsapp',
        name: 'WhatsApp (R$)',
        unit: 'money',
        data: whatsappMicros ? whatsappMicros.map(microsToUnits) : [],
        totalMicros: sum(whatsappMicros),
        color: this.token('--sms-accent-cool', FALLBACK_ACCENT_COOL),
      },
      {
        id: 'ai',
        name: 'IA (R$)',
        unit: 'money',
        data: aiMicros ? aiMicros.map(microsToUnits) : [],
        totalMicros: sum(aiMicros),
        color: this.token('--sms-accent-violet', FALLBACK_ACCENT_VIOLET),
      },
      ...this.rcsChartSeries(),
    ];

    // Só o que o cabeçalho oferece (ligações somem no internacional), só o canal
    // escolhido, e série só com a fonte na mão — a de R$ sem o resumo da carteira, ou a de contagem
    // sem `daily-sents`, zerada afirmaria "nada" sobre o que a tela não conseguiu ler.
    const offered = this.channelOptions.map(option => option.id);
    const wanted = channels.filter(channel =>
      offered.includes(channel.id)
      && (this.channel === 'all' ? !channel.onlyWhenSelected : this.channel === channel.id)
      && (channel.unit === 'count' || this.usageSummary !== null)
    );
    const visible = wanted.filter(channel =>
      channel.unit === 'count' ? channel.data !== null : this.usageSummary !== null
    );
    this.chartCountUnavailable = wanted.some(channel => channel.unit === 'count' && channel.data === null);

    const countSeries = visible.filter(channel => channel.unit === 'count');
    const moneySeries = visible.filter(channel => channel.unit === 'money');

    this.chartHasCountSeries = countSeries.length > 0;
    this.chartHasMoneySeries = moneySeries.length > 0;
    this.periodTotal = countSeries
      .filter(channel => channel.inTotal !== false)
      .reduce((total, channel) => total + sum(channel.data), 0);
    this.periodDailyAverage = labels.length ? Math.round(this.periodTotal / labels.length) : 0;
    this.periodSpentMicros = moneySeries.reduce((total, channel) => total + (channel.totalMicros || 0), 0);
    this.chartMoneyChannels = moneySeries.map(channel => channel.name.replace(' (R$)', ''));

    const mutedColor = this.token('--sms-text-muted', FALLBACK_MUTED);
    const gridColor = this.token('--sms-border-soft', FALLBACK_BORDER_SOFT);

    const axisLabels = (unit: ChartSeriesUnit) => ({
      style: {
        colors: mutedColor,
        fontSize: '11px',
      },
      formatter: unit === 'money'
        ? (value: number) => formatMoney(value, 2)
        : (value: number) => `${Math.round(value)}`,
    });

    // Um eixo por série, amarrado por `seriesName` à primeira da mesma unidade:
    // contagens dividem o eixo da esquerda e R$ o da direita. Sem série nenhuma
    // o gráfico ainda precisa de um eixo, senão o apexcharts não desenha a grade.
    const yaxis: ApexYAxis[] = visible.length
      ? visible.map(channel => {
        const anchor = channel.unit === 'count' ? countSeries[0] : moneySeries[0];

        return {
          seriesName: anchor.name,
          show: anchor === channel,
          opposite: channel.unit === 'money' && countSeries.length > 0,
          labels: axisLabels(channel.unit),
        };
      })
      : [{ labels: axisLabels('count') }];

    this.messagesChartOptions = {
      colors: visible.map(channel => channel.color),
      series: visible.map(channel => ({ name: channel.name, data: channel.data })),
      chart: {
        type: 'area',
        height: 320,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        background: 'transparent',
        fontFamily: 'inherit',
        foreColor: mutedColor,
      },
      // Curva suave e traço grosso sobre preenchimento que desaparece na base:
      // é a assinatura do gráfico do protótipo, e o que separa a área do fundo
      // sem precisar de moldura.
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      // O rótulo em cima de cada ponto empastelava a linha; o valor exato agora
      // vem do tooltip, que é onde alguém vai procurá-lo.
      dataLabels: {
        enabled: false,
      },
      grid: {
        show: true,
        borderColor: gridColor,
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { top: 0, right: 12, bottom: 0, left: 4 },
      },
      xaxis: {
        type: 'category',
        categories: labels,
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: false },
        labels: {
          style: {
            colors: mutedColor,
            fontSize: '11px',
          },
        },
      },
      yaxis,
      // As cores do balão vêm do SCSS do componente (tokens), não do tema do
      // apexcharts: o `dark` dele pinta o fundo quase igual ao do cartão e o
      // balão sumia (C5). O `theme` fica só para o apexcharts não aplicar o
      // claro por padrão por baixo do nosso.
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        theme: 'dark',
        y: {
          formatter: (value: number, options?: { seriesIndex?: number }) => {
            const hoveredSeries = options && typeof options.seriesIndex === 'number' ? visible[options.seriesIndex] : null;

            return hoveredSeries && hoveredSeries.unit === 'money'
              ? formatMoney(value, 4)
              : Math.round(value || 0).toLocaleString(MONEY_DEFAULT_LOCALE);
          },
        },
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'left',
        fontSize: '11px',
        markers: { radius: 12 },
        itemMargin: { horizontal: 10 },
        labels: {
          colors: mutedColor,
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.35,
          opacityTo: 0,
          stops: [0, 95],
        },
      },
    };

    // Mostra o seletor de data
    this.messagesChartDateField = true;
  }

  /**
   * Séries do RCS no gráfico. Em "Tudo" entra uma linha (enviados por RCS) ao lado dos
   * outros canais; com o canal RCS escolhido entram também os lidos e o SMS de fallback.
   * O total do período no canal RCS é RCS + SMS de fallback: os lidos já estão nos
   * enviados. Entregues e cliques ficam nos números do bloco RCS, para o gráfico não
   * virar um novelo de cinco linhas.
   */
  private rcsChartSeries(): ChartChannelSeries[] {
    const rcs = this.rcsSeries;

    if (!rcs) { return []; }

    const series: ChartChannelSeries[] = [
      {
        id: 'rcs',
        name: 'RCS',
        unit: 'count',
        data: rcs.sent,
        color: this.token('--sms-text-soft', FALLBACK_TEXT_SOFT),
      },
      {
        id: 'rcs',
        name: 'RCS lidos',
        unit: 'count',
        data: rcs.read,
        color: this.token('--sms-accent-cool', FALLBACK_ACCENT_COOL),
        onlyWhenSelected: true,
        inTotal: false,
      },
      {
        id: 'rcs',
        name: 'Por SMS (sem RCS)',
        unit: 'count',
        data: rcs.sms_fallback,
        color: this.token('--sms-accent-warm', FALLBACK_ACCENT_WARM),
        onlyWhenSelected: true,
      },
    ];

    // Sem o retorno do broker não há "lido": a linha seria um zero enganoso.
    return this.rcsTracked ? series : series.filter(item => item.name !== 'RCS lidos');
  }

  onDateSelected(event: MatDatepickerInputEvent<Date>) {
    this.startedDay = event.value;
    this.endedDay = new Date(this.startedDay);
    this.endedDay.setDate(this.startedDay.getDate() + 7);
    this.loadPeriod();
  }

  /**
   * Cor vinda de token. `var(--sms-*)` não vale como atributo de SVG e o
   * apexcharts escreve a cor direto no `fill`, então o valor é lido do elemento
   * — que herda a classe de versão do shell e portanto já traz a cor certa.
   */
  private token(name: string, fallback: string): string {
    try {
      const value = getComputedStyle(this.element.nativeElement).getPropertyValue(name);
      return value.trim() || fallback;
    } catch {
      return fallback;
    }
  }

  /**
   * Os dias do gráfico, em `yyyy-mm-dd`, para casar com `days[].date` do resumo.
   * A API das contagens devolve só o dia da semana como rótulo, mas cada ponto é
   * `startedDay + i` (ver `UserService::listDailySent`). As chaves saem no fuso do
   * resumo (Brasília), o mesmo de `from`/`to`: num navegador em outro fuso, a chave
   * local cairia fora do período pedido perto da meia-noite. Só as séries em R$
   * usam estas chaves; as contagens são posicionais e não passam por aqui.
   */
  private chartDayKeys(count: number): string[] {
    const keys: string[] = [];

    for (let index = 0; index < count; index++) {
      const day = new Date(this.startedDay);
      day.setDate(this.startedDay.getDate() + index);
      keys.push(this.summaryDate(day));
    }

    return keys;
  }

  /**
   * Gasto líquido do serviço por dia, em micros — já somado no servidor
   * (`/wallet/usage-summary`, 17tm466t0ca), não varrendo o extrato aqui. O
   * dia do resumo é civil em America/Sao_Paulo, o mesmo fuso de `chartDayKeys`.
   * `null` quando o resumo não veio; dia fora do resumo (ex. período maior que
   * o pedido) conta como zero. Resumo sem `days` (ou dia sem `services`) também
   * é zero: roda em todo redesenho, e lançar aqui derrubava as séries de
   * contagem junto.
   */
  private dailySpendMicros(service: WalletUsageService, days: string[]): number[] | null {
    if (!this.usageSummary) { return null; }

    const byDay = new Map((this.usageSummary.days ?? []).map(day => [day?.date, day?.services?.[service]?.spent_micros ?? 0] as [string, number]));

    return days.map(day => byDay.get(day) ?? 0);
  }

  /** `yyyy-mm-dd` do instante em America/Sao_Paulo — o fuso do contrato do resumo. */
  private summaryDate(date: Date): string {
    const parts = SUMMARY_DATE_FORMAT.formatToParts(date);
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find(item => item.type === type)?.value;

    return `${part('year')}-${part('month')}-${part('day')}`;
  }

  private formatDate(date: Date): string {
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  }
}

function microsToUnits(micros: number): number {
  return micros / 1_000_000;
}

/** R$ para eixo e balão. O eixo fica em 2 casas; o balão sobe a 4 para a IA. */
function formatMoney(value: number, maxFractionDigits: number): string {
  return moneyPipe.transform(Math.round((value || 0) * 1_000_000), 'BRL', MONEY_DEFAULT_LOCALE, maxFractionDigits);
}

/** "42%" da parte sobre o todo; `—` sem base (nada enviado no período). */
function share(part: number, whole: number): string {
  if (!whole) { return '—'; }

  return `${Math.round(((part || 0) / whole) * 100).toLocaleString(MONEY_DEFAULT_LOCALE)}%`;
}

function sum(values: number[]): number {
  return (values || []).reduce((total, value) => total + (value || 0), 0);
}
